"use client";

/**
 * StructuredData Component
 * Injects JSON-LD structured data into pages for SEO
 *
 * Usage:
 * - Global schemas (Organization, WebSite) are added in layout.tsx
 * - Page-specific schemas are added in individual page components
 *
 * Example:
 * <StructuredData type="service" serviceId="as" />
 * <StructuredData type="faq" faqs={faqData} />
 * <StructuredData type="article" article={articleData} />
 */

import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import {
  organizationSchema,
  websiteSchema,
  serviceSchemas,
  localBusinessSchema,
  getFAQSchema,
  getArticleSchema,
  getBreadcrumbSchema,
} from "@/lib/schema";

type StructuredDataProps =
  | { type: "global" }
  | { type: "service"; serviceId: string }
  | { type: "localBusiness" }
  | {
      type: "faq";
      faqs: Array<{ question: string; answer: string }>;
      /** Keep schema in the content language on English-only blog wrappers. */
      translate?: boolean;
    }
  | {
      type: "article";
      article: {
        title: string;
        description: string;
        url: string;
        datePublished: string;
        dateModified?: string;
        image?: string;
        tags?: string[];
      };
    }
  | {
      type: "breadcrumb";
      items: Array<{ name: string; url: string }>;
    };

export default function StructuredData(props: StructuredDataProps) {
  const locale = useLocale();
  let schemas: object[] = [];

  switch (props.type) {
    case "global":
      // Organization + WebSite + LocalBusiness schemas for global use
      schemas = [organizationSchema, websiteSchema, localBusinessSchema];
      break;

    case "service":
      // Service schema based on serviceId
      const serviceSchema = serviceSchemas[props.serviceId];
      if (serviceSchema) {
        schemas = [serviceSchema];
      }
      break;

    case "localBusiness":
      schemas = [localBusinessSchema];
      break;

    case "faq":
      // Translate to the page locale so /ms and /zh ship FAQPage JSON-LD in the
      // same language as the visible accordion (ServiceFaq already calls t()).
      // t() returns its input when no key matches, so callers that pass
      // already-translated FAQs are unaffected.
      schemas = [
        getFAQSchema(
          props.translate === false
            ? props.faqs
            : props.faqs.map((f) => ({
                question: t(locale, f.question),
                answer: t(locale, f.answer),
              })),
        ),
      ];
      break;

    case "article":
      schemas = [getArticleSchema(props.article)];
      break;

    case "breadcrumb":
      schemas = [getBreadcrumbSchema(props.items)];
      break;
  }

  if (schemas.length === 0) {
    return null;
  }

  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}

